import React, { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Eye, FileText, Image as ImageIcon, ExternalLink } from "lucide-react";

const isImage = (ct = "", name = "") =>
  /^image\//.test(ct) || /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)$/i.test(name);
const isPDF = (ct = "", name = "") => /pdf/i.test(ct) || /\.pdf$/i.test(name);

/**
 * Inline preview thumbnail + click-to-open viewer dialog.
 * Props: fileId, name (optional), className, showName, compact
 */
export function DocumentTile({ fileId, name, className = "", showName = true, compact = false }) {
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!fileId) return;
    let active = true;
    api.get(`/files/${fileId}/meta`)
      .then((r) => { if (active) setMeta(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [fileId]);

  if (!fileId) return null;
  const filename = name || meta?.original_filename || "document";
  const ctype = meta?.content_type || "";
  const img = isImage(ctype, filename);
  const pdf = isPDF(ctype, filename);

  const downloadFile = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    try {
      const res = await api.get(`/files/${fileId}?download=1`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <>
      <div className={`relative group border border-[var(--border)] rounded-md overflow-hidden bg-zinc-50 ${className}`}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="block w-full text-left"
          data-testid={`doc-tile-${fileId}`}
          title={`Deschide ${filename}`}
        >
          {img ? (
            <img src={fileUrl(fileId)} alt={filename} className={`w-full ${compact ? "h-24" : "h-40"} object-cover`} />
          ) : (
            <div className={`w-full ${compact ? "h-24" : "h-40"} flex flex-col items-center justify-center text-zinc-500 px-2`}>
              {pdf ? <FileText size={compact ? 28 : 40} className="text-red-500" /> : <FileText size={compact ? 28 : 40} />}
              <div className="text-[10px] uppercase tracking-wider mt-1">{pdf ? "PDF" : (filename.split(".").pop() || "doc").toUpperCase()}</div>
            </div>
          )}
          {showName && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1">
              <div className="text-[10px] text-white truncate">{filename}</div>
            </div>
          )}
        </button>
        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            className="bg-white/95 p-1 rounded-full shadow text-zinc-700 hover:text-[var(--primary)]"
            title="Vizualizează"
            data-testid={`doc-view-${fileId}`}
          >
            <Eye size={13} />
          </button>
          <button
            type="button"
            onClick={downloadFile}
            className="bg-white/95 p-1 rounded-full shadow text-zinc-700 hover:text-green-600"
            title="Descarcă"
            data-testid={`doc-dl-${fileId}`}
          >
            <Download size={13} />
          </button>
        </div>
      </div>

      <DocumentViewer fileId={fileId} name={filename} ctype={ctype} open={open} onOpenChange={setOpen} onDownload={downloadFile} />
    </>
  );
}

export function DocumentViewer({ fileId, name = "document", ctype = "", open, onOpenChange, onDownload }) {
  const img = isImage(ctype, name);
  const pdf = isPDF(ctype, name);
  const url = fileUrl(fileId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 truncate pr-8">
            {img ? <ImageIcon size={16}/> : <FileText size={16}/>}
            <span className="truncate">{name}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="bg-zinc-100 rounded-md overflow-hidden flex items-center justify-center" style={{ minHeight: "60vh", maxHeight: "75vh" }}>
          {img ? (
            <img src={url} alt={name} className="max-w-full max-h-[75vh] object-contain" />
          ) : pdf ? (
            <iframe src={url} title={name} className="w-full" style={{ minHeight: "70vh" }} />
          ) : (
            <div className="text-center p-8">
              <FileText size={56} className="mx-auto text-zinc-400" />
              <div className="text-sm text-zinc-600 mt-3">Previzualizare indisponibilă pentru acest tip de fișier.</div>
              <div className="text-xs text-zinc-400 mt-1">{name}</div>
              <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[var(--primary)] mt-3 hover:underline">
                <ExternalLink size={14}/> Deschide într-un tab nou
              </a>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-[var(--primary)]">
            <ExternalLink size={14}/> Deschide într-un tab nou
          </a>
          <Button onClick={onDownload} data-testid="viewer-download" className="gap-1.5">
            <Download size={14}/> Descarcă
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
